import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Mail, ArrowRight, ArrowLeft, Shield, MailCheck } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import AuthFooter from '../components/AuthFooter';

type Phase = 'enter-email' | 'sent';

export default function ForgotPassword() {
  const { requestPasswordReset } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const prefilledEmail = searchParams.get('email')?.trim().toLowerCase() ?? '';

  const [phase, setPhase] = useState<Phase>('enter-email');
  const [email, setEmail] = useState(prefilledEmail);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim().toLowerCase();
    if (!validEmail(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      await requestPasswordReset(trimmed);
      setEmail(trimmed);
      setPhase('sent');
    } catch (err) {
      setError(humaniseError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B2341] via-[#1A3A5C] to-[#0B2341] grid place-items-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex flex-col items-center gap-3 mb-4">
            <img
              src="https://adiimmigration.com.au/wp-content/uploads/2021/06/ADI_Immigration-white.png"
              alt="ADI Immigration"
              className="w-24 h-24"
            />
            <div className="text-slate-300 text-sm">Client Portal</div>
          </div>
          <h1 className="text-white text-2xl font-semibold mb-1">Reset your password</h1>
          <p className="text-slate-300 text-sm">
            {phase === 'enter-email'
              ? "Enter your email and we'll send you a link to set a new password."
              : 'Check your inbox.'}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {phase === 'enter-email' && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="form-label">Email address</label>
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="form-input pl-10"
                    required
                    autoFocus
                    autoComplete="email"
                  />
                </div>
              </div>

              {error && <ErrorBanner message={error} />}

              <button type="submit" disabled={loading} className="btn-primary w-full justify-center">
                {loading ? 'Sending...' : <>Send reset link <ArrowRight size={16} /></>}
              </button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
                >
                  <ArrowLeft size={12} /> Back to sign in
                </Link>
              </div>
            </form>
          )}

          {phase === 'sent' && (
            <div className="space-y-5">
              <div className="flex flex-col items-center text-center gap-3 py-2">
                <div className="rounded-full bg-blue-50 p-3">
                  <MailCheck size={28} className="text-[#2CABEA]" />
                </div>
                <p className="text-sm text-slate-700">
                  If an account exists for <span className="font-medium">{email}</span>, we've sent a
                  link to set a new password. It expires in 24 hours.
                </p>
                <p className="text-xs text-slate-500">
                  Can't find it? Check your spam folder, or try again in a minute.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setPhase('enter-email')}
                className="btn-secondary w-full justify-center"
              >
                Use a different email
              </button>

              <button
                type="button"
                onClick={() => navigate('/login')}
                className="btn-primary w-full justify-center"
              >
                Back to sign in <ArrowRight size={16} />
              </button>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-slate-100 text-center">
            <p className="text-xs text-slate-500 flex items-center justify-center gap-1.5">
              <Shield size={12} />
              Secured sign-in. We never share your data.
            </p>
          </div>
        </div>

        <div className="mt-6 text-center text-slate-400 text-xs">
          <p>
            Need help? Call{' '}
            <a href="tel:1300729331" className="text-[#2CABEA] hover:underline">
              1300 729 331
            </a>{' '}
            or email{' '}
            <a href="mailto:adi@adibusinessgroup.com" className="text-[#2CABEA] hover:underline">
              adi@adibusinessgroup.com
            </a>
          </p>
        </div>

        <AuthFooter />
      </div>
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
      {message}
    </div>
  );
}

function humaniseError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err ?? 'Something went wrong.');
  const lower = raw.toLowerCase();
  if (lower.includes('rate') || lower.includes('too many')) {
    return 'Too many attempts. Please wait a minute and try again.';
  }
  if (lower.includes('invalid') && lower.includes('email')) {
    return 'That email address looks off. Please check and try again.';
  }
  return raw;
}
